import { type InviteChannel, inviteAddressProblem, looksLikeEmail } from '@echo/core';
import {
  addReadyInvite,
  removeReadyInvite,
  useGroupInvites,
  useReadyInvites,
  useSendInvites,
} from '@echo/core/react';
import { Button, Chip, SegmentedControl, TextField } from '@echo/ui-web';
import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { CircleAlert, CircleX, Settings2 } from 'lucide-react';
import { useId, useState } from 'react';
import { ToolbarBack } from '@/components/empty-state-frame';
import { GroupActionFailed } from '@/components/group-load-state';
import { useRoleLabels } from '@/components/group-role-tag';
import { InfoIcon } from '@/components/info-icon';
import { PhoneField, type PhoneValue } from '@/components/phone-field';
import { useGoBack } from '@/lib/use-go-back';

export const Route = createFileRoute('/_app/account_/groups_/$id_/invite')({
  component: InvitePeoplePage,
});

/**
 * Manage group › Invite people (Figma Invite): by email (the default) or phone, one person at a
 * time into Ready to invite — each as a Member unless "Invite as" changes it — then "Send N
 * invites": Core messages each one a link to join. Someone already on the list or already invited
 * isn't added twice, and the field says which.
 */
function InvitePeoplePage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const goBack = useGoBack(
    () => void navigate({ to: '/account/groups/$id', params: { id }, replace: true }),
  );
  const roleLabel = useRoleLabels();
  const { id } = Route.useParams();
  const pending = useGroupInvites(id).data ?? [];
  const ready = useReadyInvites(id);
  const sending = useSendInvites(id);
  const [channel, setChannel] = useState<InviteChannel>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState<PhoneValue>({ e164: '', valid: false });
  // A new key empties the phone field once its number is on the list.
  const [phoneKey, setPhoneKey] = useState(0);

  const address = channel === 'email' ? email.trim() : phone.e164;
  const valid = channel === 'email' ? looksLikeEmail(email) : phone.valid;
  const problem = valid ? inviteAddressProblem(address, ready, pending) : undefined;
  const canAdd = valid && problem === undefined;
  const duplicate = {
    email: {
      ready: t`You've already added that email address.`,
      pending: t`That email address is already invited.`,
    },
    phone: {
      ready: t`You've already added that phone number.`,
      pending: t`That phone number is already invited.`,
    },
  };
  const error = problem === undefined ? undefined : duplicate[channel][problem];
  const errorIcon = <CircleAlert size={16} aria-hidden />;
  const add = () => {
    if (!canAdd) return;
    addReadyInvite(id, channel, address, pending);
    if (channel === 'email') setEmail('');
    else {
      setPhone({ e164: '', valid: false });
      setPhoneKey((key) => key + 1);
    }
  };
  const send = () => {
    if (ready.length === 0 || sending.isPending) return;
    sending.mutate(ready, {
      onSuccess: (sent) =>
        void navigate({
          to: '/account/groups/$id/invite/sent',
          params: { id },
          search: { sent },
          replace: true,
        }),
    });
  };
  const count = ready.length;

  return (
    <>
      <ToolbarBack
        render={({ className, children }) => (
          <button type="button" className={className} onClick={goBack}>
            {children}
          </button>
        )}
      />
      <h1 className="text-display-sm font-bold">
        <Trans>Invite people</Trans>
      </h1>
      <div className="flex flex-col gap-section-sm pt-content-md">
        <form
          noValidate
          className="flex flex-col gap-content-lg rounded-xl border border-border-weak bg-surface p-content-sm"
          onSubmit={(event) => {
            event.preventDefault();
            add();
          }}
        >
          <div className="flex flex-col gap-content-sm">
            <p className="text-body-md text-on-surface-medium">
              <Trans>Invite people to your group. They'll get a message with a link to join.</Trans>
            </p>
            <SegmentedControl
              options={[
                { value: 'email', label: t`Email` },
                { value: 'phone', label: t`Phone` },
              ]}
              value={channel}
              onChange={setChannel}
            />
          </div>
          <div className="flex flex-col gap-content-sm">
            {channel === 'email' ? (
              <TextField
                label={t`Email address`}
                placeholder={t`e.g. name@email.com`}
                type="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="email"
                spellCheck={false}
                error={error}
                errorIcon={errorIcon}
                value={email}
                onChange={(event) => setEmail(event.currentTarget.value)}
              />
            ) : (
              <PhoneField
                key={phoneKey}
                label={t`Phone`}
                placeholder={t`700 000 0000`}
                error={error}
                errorIcon={errorIcon}
                onChange={setPhone}
              />
            )}
            <Button type="submit" variant="secondary" disabled={!canAdd}>
              {t`Add`}
            </Button>
          </div>
          <p className="flex gap-internal-md text-body-md text-on-surface-medium">
            <InfoIcon />
            <Trans>You can add one person at a time, then send invites all together.</Trans>
          </p>
        </form>

        {count > 0 ? (
          <section className="flex flex-col gap-content-lg rounded-xl bg-surface-tertiary p-content-sm">
            <h2 className="text-title-lg font-bold">
              <Trans>Ready to invite ({count})</Trans>
            </h2>
            <ul className="flex flex-col gap-content-lg">
              {ready.map(({ id: inviteId, address: invited, role }) => (
                <ReadyInvite
                  key={inviteId}
                  groupId={id}
                  inviteId={inviteId}
                  address={invited}
                  role={roleLabel[role]}
                />
              ))}
            </ul>
          </section>
        ) : null}

        <div className="flex flex-col items-end gap-content-sm">
          {sending.isError ? <GroupActionFailed /> : null}
          <Button disabled={count === 0} loading={sending.isPending} onClick={send}>
            {plural(count, { one: 'Send # invite', other: 'Send # invites' })}
          </Button>
        </div>
      </div>
    </>
  );
}

/**
 * One person on Ready to invite: the address with its remove button, and "Invite as:" with the
 * access they will have, which opens Access level. That link is named "Invite as: Member" and
 * described by the address, so each one says whose it is.
 */
function ReadyInvite({
  groupId,
  inviteId,
  address,
  role,
}: {
  groupId: string;
  inviteId: string;
  address: string;
  role: string;
}) {
  const { t } = useLingui();
  const ids = useId();
  return (
    <li className="flex flex-col gap-content-sm rounded-xxl border border-border-weak bg-surface p-content-sm">
      <div className="flex items-center gap-internal-lg">
        <span id={`${ids}-address`} className="min-w-0 flex-1 truncate text-label-md">
          {address}
        </span>
        <button
          type="button"
          aria-label={t`Remove ${address}`}
          onClick={() => removeReadyInvite(groupId, inviteId)}
          className="inline-flex size-tap-target shrink-0 cursor-pointer items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-highlight"
        >
          <CircleX size={24} className="fill-action-primary text-on-action-primary" aria-hidden />
        </button>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-internal-md">
        <span id={`${ids}-as`} className="text-body-sm text-on-surface-medium">
          <Trans>Invite as:</Trans>
        </span>
        <Chip
          variant="assist"
          render={
            <Link
              to="/account/groups/$id/invite/access"
              params={{ id: groupId }}
              search={{ inviteId }}
            />
          }
          aria-labelledby={`${ids}-as ${ids}-role`}
          aria-describedby={`${ids}-address`}
        >
          <Settings2 size={20} aria-hidden />
          <span id={`${ids}-role`}>{role}</span>
        </Chip>
      </div>
    </li>
  );
}
